import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createUuid } from '@notifi/uuid/browser';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowDown,
  ArrowUp,
  CalendarDays,
  Camera,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  Gauge,
  GripVertical,
  Hash,
  Library,
  ListChecks,
  ListFilter,
  Lock,
  MapPin,
  MoreHorizontal,
  Paperclip,
  Plus,
  Repeat2,
  ScanLine,
  Settings2,
  Signature,
  Trash2,
  Type,
  X,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms/tooltip';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import { LeadFieldPicker, withOptionRemoved, withOptionRenamed } from './LeadFieldPicker';
import { QuestionBankPicker } from './QuestionBankPicker';
import { IntakePublicPageSettings } from '@/components/forms-engine/IntakePublicPageSettings';
import { defaultPublicSettingsDraft } from '@/lib/forms/public-settings';
import { useBookableServices } from '@/hooks/bookingConfig/useBookableServices';
import { useQuestionBank } from '@/hooks/forms-engine/useQuestionBank';
import {
  DEFAULT_MINUTE_STEP,
  FILE_UPLOAD_BOUNDS,
  FILE_UPLOAD_DEFAULTS,
  fileUploadLimits,
  minuteStepOf,
  TIME_MINUTE_STEPS,
} from '@/lib/forms/field-types';
import { type LeadFieldTarget } from '@/lib/forms/lead-field-targets';
import { questionToBuilderItem } from '@/lib/forms/question-bank';
import { isItemTypeAllowedOnSurface, type BuilderIssue } from '@/lib/forms/unified-form-builder';
import { cn } from '@/lib/utils';
import type {
  FormDefinitionBuilderProps,
  FormDefinitionBuilderValue,
} from './FormDefinitionBuilder';
import type { FormItem, FormItemType, FormSection } from '@/types/appointmentForm';
import type {
  FileUploadAccept,
  FormSurface,
  QuestionDefinition,
  TimeMinuteStep,
} from '@/types/formsEngine';

const ITEM_TYPES: Array<{
  value: FormItemType;
  label: string;
  description: string;
  icon: typeof Type;
  public: boolean;
}> = [
  {
    value: 'TEXT',
    label: 'Text',
    description: 'Short or long written answer',
    icon: Type,
    public: true,
  },
  {
    value: 'NUMBER',
    label: 'Number',
    description: 'Numeric value with limits',
    icon: Hash,
    public: true,
  },
  {
    value: 'MEASUREMENT',
    label: 'Measurement',
    description: 'Value plus a unit',
    icon: Gauge,
    public: false,
  },
  {
    value: 'CHECKBOX',
    label: 'Checkbox',
    description: 'A task to check off',
    icon: Check,
    public: true,
  },
  {
    value: 'YES_NO',
    label: 'Yes / No',
    description: 'Two clear choices',
    icon: CheckCheck,
    public: true,
  },
  {
    value: 'PASS_FAIL',
    label: 'Pass / Fail',
    description: 'Inspection result',
    icon: ListChecks,
    public: false,
  },
  {
    value: 'SINGLE_SELECT',
    label: 'Single choice',
    description: 'Pick one option',
    icon: ListFilter,
    public: true,
  },
  {
    value: 'MULTI_SELECT',
    label: 'Multi choice',
    description: 'Pick several options',
    icon: ListChecks,
    public: true,
  },
  { value: 'DATE', label: 'Date', description: 'Calendar date', icon: CalendarDays, public: true },
  { value: 'TIME', label: 'Time', description: 'Time of day', icon: Clock, public: true },
  {
    value: 'ADDRESS',
    label: 'Address',
    description: 'Street, city, state and postal code',
    icon: MapPin,
    public: true,
  },
  {
    value: 'FILE_UPLOAD',
    label: 'File upload',
    description: 'Customer uploads photos or PDFs',
    icon: Paperclip,
    public: true,
  },
  {
    value: 'IMAGE_UPLOAD',
    label: 'Photo',
    description: 'Capture or upload evidence',
    icon: Camera,
    public: false,
  },
  {
    value: 'SIGNATURE',
    label: 'Signature',
    description: 'Drawn signature with signer',
    icon: Signature,
    public: true,
  },
  {
    value: 'BARCODE_SCAN',
    label: 'Barcode scan',
    description: 'Scan an equipment code',
    icon: ScanLine,
    public: false,
  },
];

type DraggedItem =
  | { kind: 'new'; type: FormItemType }
  | { kind: 'bank'; question: QuestionDefinition }
  | { kind: 'existing'; index: number };
type DragTarget = DraggedItem | { kind: 'end' };

/** Stable identity so the focus effect does not re-run on every render. */
const EMPTY_ISSUES: BuilderIssue[] = [];

export function FormDefinitionOutlineBuilder({
  value,
  onChange,
  hideVisitOptions = false,
  surface = hideVisitOptions ? 'BOOKING' : 'VISIT_FORM',
  issues = EMPTY_ISSUES,
  excludeTypes,
  intakeLeadSource,
  version,
}: FormDefinitionBuilderProps) {
  const { data: bookableServices = [] } = useBookableServices();
  const { data: bankQuestions = [], isLoading: bankQuestionsLoading } = useQuestionBank({
    status: 'ACTIVE',
  });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [selectedSection, setSelectedSection] = useState(0);
  // An intake form starts collapsed: its first row is the Name contact field,
  // and opening on it made the whole form read as uneditable. Other surfaces
  // keep opening on their first question.
  const [expandedItem, setExpandedItem] = useState<number | null>(surface === 'INTAKE' ? null : 0);
  const [bankOpen, setBankOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeDrag, setActiveDrag] = useState<DraggedItem | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const section = value.sections[selectedSection];
  const nameIssues = issues.filter((issue) => issue.field === 'name');
  const sectionsIssue = issues.find((issue) => issue.field === 'sections');
  const issuesForSection = (index: number) =>
    issues.filter((issue) => issue.sectionIndex === index && issue.itemIndex === undefined);
  const issuesForItem = (sectionIndex: number, itemIndex: number) =>
    issues.filter((issue) => issue.sectionIndex === sectionIndex && issue.itemIndex === itemIndex);
  const issueCountForSection = (index: number) =>
    issues.filter((issue) => issue.sectionIndex === index).length;

  // Open the first section that has a problem, then bring the first field with
  // one into view. Items in unselected sections are not rendered at all, so
  // without the first step the message would have nowhere to appear.
  useEffect(() => {
    if (issues.length === 0) return;
    const located = issues.find((issue) => issue.sectionIndex !== undefined);
    if (located?.sectionIndex !== undefined) {
      setSelectedSection(located.sectionIndex);
      if (located.itemIndex !== undefined) setExpandedItem(located.itemIndex);
      setSettingsOpen(false);
    }
    const frame = requestAnimationFrame(() => {
      rootRef.current
        ?.querySelector('[data-issue-anchor]')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      // And put the cursor in the first field that needs fixing, so the fix
      // can be typed straight away — the name, most often.
      rootRef.current
        ?.querySelector<HTMLElement>('[aria-invalid="true"]')
        ?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [issues]);
  // A new form starts at its name: the cursor is there, ready to type.
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!value.name) nameRef.current?.focus();
    // Only when the builder opens, not every time the name is cleared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Every form offers every item type, whoever fills it in: the customer's
  // form page renders them all. A booking survey and an intake form are
  // answered inside flows that render a smaller set.
  const allowedTypes = ITEM_TYPES.filter(
    (type) =>
      (surface === 'BOOKING' || surface === 'INTAKE' ? type.public : true) &&
      (surface !== 'BOOKING' || type.value !== 'SIGNATURE') &&
      isItemTypeAllowedOnSurface(type.value, surface) &&
      !excludeTypes?.includes(type.value),
  );
  const quickQuestions = useMemo(() => {
    const allowedTypeValues = new Set(allowedTypes.map((type) => type.value));
    return bankQuestions
      .filter((question) => {
        const item = questionToBuilderItem(question, 0);
        return item ? allowedTypeValues.has(item.type) : false;
      })
      .sort(
        (left, right) =>
          (right.usageCount ?? 0) - (left.usageCount ?? 0) || left.name.localeCompare(right.name),
      )
      .slice(0, 4);
  }, [allowedTypes, bankQuestions]);
  const itemCount = value.sections.reduce((total, candidate) => total + candidate.items.length, 0);
  // Every lead field already claimed on this template, target -> the
  // claiming question's label. Passed to every LeadFieldPicker so it can
  // disable a target someone else already owns — the server rejects two
  // questions racing for the same field (`assertIntakeLeadMapping`).
  const leadFieldUsage = useMemo(() => {
    const usage: Partial<Record<LeadFieldTarget, string>> = {};
    for (const candidateSection of value.sections) {
      for (const candidateItem of candidateSection.items) {
        const target = candidateItem.config?.leadField;
        if (target) usage[target] = candidateItem.label || 'Untitled item';
      }
    }
    return usage;
  }, [value.sections]);

  useEffect(() => {
    if (selectedSection >= value.sections.length) {
      setSelectedSection(Math.max(0, value.sections.length - 1));
      setExpandedItem(0);
      return;
    }
    const selected = value.sections[selectedSection];
    if (selected && expandedItem !== null && expandedItem >= selected.items.length) {
      setExpandedItem(selected.items.length ? selected.items.length - 1 : null);
    }
  }, [expandedItem, selectedSection, value.sections]);

  function update(patch: Partial<FormDefinitionBuilderValue>) {
    onChange({ ...value, ...patch });
  }

  function updateSection(index: number, patch: Partial<FormSection>) {
    update({
      sections: value.sections.map((candidate, candidateIndex) =>
        candidateIndex === index ? { ...candidate, ...patch } : candidate,
      ),
    });
  }

  function updateItem(index: number, patch: Partial<FormItem>) {
    if (!section) return;
    updateSection(selectedSection, {
      items: section.items.map((candidate, candidateIndex) =>
        candidateIndex === index ? { ...candidate, ...patch } : candidate,
      ),
    });
  }

  function addSection() {
    const nextIndex = value.sections.length;
    update({
      sections: [
        ...value.sections,
        {
          id: createUuid(),
          name: `Section ${nextIndex + 1}`,
          sortOrder: nextIndex,
          repeatable: false,
          items: [],
        },
      ],
    });
    setSelectedSection(nextIndex);
    setExpandedItem(null);
  }

  function removeSection(index: number) {
    if (value.sections.length <= 1) return;
    update({
      sections: value.sections
        .filter((_, candidateIndex) => candidateIndex !== index)
        .map((candidate, candidateIndex) => ({ ...candidate, sortOrder: candidateIndex })),
    });
    setSelectedSection(Math.max(0, Math.min(index, value.sections.length - 2)));
    setExpandedItem(0);
  }

  function addItem(type: FormItemType, requestedIndex = section?.items.length ?? 0) {
    if (!section) return;
    const nextIndex = Math.max(0, Math.min(requestedIndex, section.items.length));
    // An id from the start: the server matches saved items by id, so an
    // id-less item is a new question, and drag ids stay stable.
    const next: FormItem = {
      id: createUuid(),
      label: '',
      type,
      required: false,
      sortOrder: nextIndex,
      description: '',
      photoRequired: false,
      minPhotos: 1,
      config: {
        ...(defaultConfig(type) ?? {}),
        ...(surface === 'INTAKE' && type === 'TEXT'
          ? { textStyle: 'short' as const, inputType: 'text' as const }
          : {}),
      },
    };
    const items = [...section.items];
    items.splice(nextIndex, 0, next);
    updateSection(selectedSection, {
      items: items.map((candidate, candidateIndex) => ({
        ...candidate,
        sortOrder: candidateIndex,
      })),
    });
    setSettingsOpen(false);
    setExpandedItem(nextIndex);
  }

  function addFromBank(question: QuestionDefinition, requestedIndex = section?.items.length ?? 0) {
    if (!section) return;
    const nextIndex = Math.max(0, Math.min(requestedIndex, section.items.length));
    const linked = questionToBuilderItem(question, nextIndex);
    if (!linked) return;
    const items = [...section.items];
    items.splice(nextIndex, 0, { ...linked, id: createUuid() });
    updateSection(selectedSection, {
      items: items.map((candidate, candidateIndex) => ({
        ...candidate,
        sortOrder: candidateIndex,
      })),
    });
    setSettingsOpen(false);
    setExpandedItem(nextIndex);
    setBankOpen(false);
  }

  function removeItem(index: number) {
    if (!section) return;
    if (section.items[index]?.config?.locked) return;
    updateSection(selectedSection, {
      items: section.items
        .filter((_, candidateIndex) => candidateIndex !== index)
        .map((candidate, candidateIndex) => ({ ...candidate, sortOrder: candidateIndex })),
    });
    setExpandedItem((current) => {
      if (current === null) return null;
      if (current === index) {
        return section.items.length > 1
          ? Math.max(0, Math.min(index, section.items.length - 2))
          : null;
      }
      return current > index ? current - 1 : current;
    });
  }

  function moveItem(index: number, direction: -1 | 1) {
    reorderItem(index, index + direction);
  }

  function reorderItem(index: number, target: number) {
    if (!section) return;
    if (target < 0 || target >= section.items.length) return;
    if (target === index) return;
    updateSection(selectedSection, {
      items: arrayMove(section.items, index, target).map((candidate, candidateIndex) => ({
        ...candidate,
        sortOrder: candidateIndex,
      })),
    });
    setExpandedItem(target);
  }

  function handleDragStart(event: DragStartEvent) {
    const dragged = event.active.data.current as DraggedItem | undefined;
    if (!dragged) return;
    setSettingsOpen(false);
    setActiveDrag(dragged);
  }

  function handleDragEnd(event: DragEndEvent) {
    const dragged = event.active.data.current as DraggedItem | undefined;
    const target = event.over?.data.current as DragTarget | undefined;
    if (dragged?.kind === 'new') {
      addItem(dragged.type, target?.kind === 'existing' ? target.index : section?.items.length);
    } else if (dragged?.kind === 'bank') {
      addFromBank(
        dragged.question,
        target?.kind === 'existing' ? target.index : section?.items.length,
      );
    } else if (dragged?.kind === 'existing' && section) {
      reorderItem(
        dragged.index,
        target?.kind === 'existing' ? target.index : section.items.length - 1,
      );
    }
    setActiveDrag(null);
  }

  function toggleBookableService(id: string) {
    update({
      bookableServiceIds: value.bookableServiceIds.includes(id)
        ? value.bookableServiceIds.filter((candidate) => candidate !== id)
        : [...value.bookableServiceIds, id],
    });
  }

  return (
    <div ref={rootRef} className="overflow-hidden rounded-xl border bg-background shadow-sm">
      <div className="border-b bg-muted/20 px-4 py-3">
        {/* The controls wrap below the name before it is squeezed unreadable. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="min-w-[12rem] flex-1">
            <Input
              ref={nameRef}
              value={value.name}
              onChange={(event) => update({ name: event.target.value })}
              placeholder={
                surface === 'INTAKE'
                  ? 'Intake form name'
                  : hideVisitOptions
                    ? 'Booking survey name'
                    : 'Template name'
              }
              aria-invalid={nameIssues.length > 0}
              className={cn(
                'h-8 min-w-0 max-w-md border-0 bg-transparent px-0 text-base font-semibold shadow-none focus-visible:ring-0',
                nameIssues.length > 0 && 'text-destructive placeholder:text-destructive/70',
              )}
            />
            {nameIssues.map((issue) => (
              <p
                key={issue.message}
                data-issue-anchor
                className="text-xs font-medium text-destructive"
              >
                {issue.message}
              </p>
            ))}
            <p className="text-xs text-muted-foreground">
              {value.sections.length} {value.sections.length === 1 ? 'section' : 'sections'} ·{' '}
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {version ? (
              <span className="text-xs text-muted-foreground">Version {version}</span>
            ) : null}
            <Button
              type="button"
              variant={settingsOpen ? 'secondary' : 'outline'}
              size="sm"
              aria-pressed={settingsOpen}
              onClick={() => setSettingsOpen((current) => !current)}
              className="gap-2"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Template settings
            </Button>
          </div>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragCancel={() => setActiveDrag(null)}
        onDragEnd={handleDragEnd}
      >
        <div className="grid xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="grid min-h-[650px] lg:grid-cols-[200px_minmax(420px,1fr)]">
            <aside className="border-b bg-muted/15 lg:border-b-0 lg:border-r">
              <div className="flex h-14 items-center justify-between border-b px-3">
                <div>
                  <p className="text-xs font-semibold">Sections</p>
                  <p className="text-[10px] text-muted-foreground">
                    {value.sections.length} · {itemCount} items
                  </p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={addSection}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="max-h-52 space-y-1 overflow-y-auto p-2 lg:max-h-[590px]">
                {sectionsIssue ? (
                  <p
                    data-issue-anchor
                    className="rounded-md bg-destructive-surface px-2 py-1.5 text-[10px] font-medium text-destructive"
                  >
                    {sectionsIssue.message}
                  </p>
                ) : null}
                {value.sections.map((candidate, index) => {
                  const active = selectedSection === index;
                  const blocking = candidate.items.filter(
                    (candidateItem) => candidateItem.required,
                  ).length;
                  return (
                    <button
                      key={candidate.id ?? `${candidate.name}:${index}`}
                      type="button"
                      onClick={() => {
                        setSelectedSection(index);
                        setExpandedItem(candidate.items.length ? 0 : null);
                        setSettingsOpen(false);
                      }}
                      className={cn(
                        'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                        active
                          ? 'border-foreground/15 bg-background shadow-sm'
                          : 'border-transparent hover:bg-muted/50',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[9px] font-semibold',
                            active
                              ? 'bg-foreground text-background'
                              : 'bg-background text-muted-foreground',
                          )}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-xs font-medium">
                          {candidate.name || 'Untitled section'}
                        </span>
                        {issueCountForSection(index) > 0 ? (
                          <span
                            aria-label={`${issueCountForSection(index)} to fix`}
                            className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-destructive-foreground"
                          >
                            {issueCountForSection(index)}
                          </span>
                        ) : null}
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                      </div>
                      <p className="mt-1 pl-7 text-[10px] text-muted-foreground">
                        {candidate.items.length} {candidate.items.length === 1 ? 'item' : 'items'}
                        {blocking ? ` · ${blocking} required` : ''}
                      </p>
                    </button>
                  );
                })}
              </div>
            </aside>

            <main className="min-w-0 border-b lg:border-b-0 lg:border-r">
              {settingsOpen ? (
                <>
                  <div className="flex h-14 items-center border-b px-4">
                    <div>
                      <p className="text-xs font-semibold">Template settings</p>
                      <p className="text-[10px] text-muted-foreground">
                        Defaults for every new instance
                      </p>
                    </div>
                  </div>
                  <TemplateSettings
                    value={value}
                    onChange={update}
                    hideVisitOptions={hideVisitOptions}
                    surface={surface}
                    bookableServices={bookableServices}
                    onToggleBookableService={toggleBookableService}
                    intakeLeadSource={intakeLeadSource}
                  />
                </>
              ) : section ? (
                <>
                  <div className="flex h-14 items-center gap-2 border-b px-4">
                    <Input
                      value={section.name}
                      onChange={(event) =>
                        updateSection(selectedSection, { name: event.target.value })
                      }
                      placeholder="Section name"
                      className="h-8 flex-1 border-0 px-0 text-sm font-semibold shadow-none focus-visible:ring-0"
                    />
                    {/* Any form can repeat a section — Room 1, Room 2… — except a
                        booking survey, which the booking flow shows once. */}
                    {surface !== 'BOOKING' ? (
                      <SectionRepeatControl
                        enabled={Boolean(section.repeatable)}
                        options={section.repeatOptions ?? []}
                        onEnabledChange={(checked) =>
                          updateSection(selectedSection, {
                            repeatable: checked,
                            repeatOptions: checked
                              ? section.repeatOptions?.length
                                ? section.repeatOptions
                                : ['']
                              : section.repeatOptions,
                          })
                        }
                        onOptionsChange={(repeatOptions) =>
                          updateSection(selectedSection, { repeatOptions })
                        }
                      />
                    ) : null}
                    {value.sections.length > 1 ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => removeSection(selectedSection)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </div>

                  <div className="min-h-[460px]">
                    {issuesForSection(selectedSection).length > 0 ? (
                      <ul className="mx-4 mt-4 space-y-1 rounded-md bg-destructive-surface px-3 py-2">
                        {issuesForSection(selectedSection).map((issue) => (
                          <li
                            key={issue.message}
                            data-issue-anchor
                            className="text-xs font-medium text-destructive"
                          >
                            {issue.message}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {section.items.length === 0 ? (
                      <CanvasDropZone empty />
                    ) : (
                      <SortableContext
                        items={section.items.map(itemDndId)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-3 p-4">
                          {section.items.map((candidate, index) => (
                            <SortableBuilderItem
                              key={itemDndId(candidate, index)}
                              id={itemDndId(candidate, index)}
                              item={candidate}
                              issues={issuesForItem(selectedSection, index)}
                              index={index}
                              itemCount={section.items.length}
                              expanded={expandedItem === index}
                              allowPhotoEvidence={surface !== 'BOOKING' && surface !== 'INTAKE'}
                              surface={surface}
                              leadFieldUsage={leadFieldUsage}
                              onToggle={() => {
                                setExpandedItem(expandedItem === index ? null : index);
                                setSettingsOpen(false);
                              }}
                              onChange={(patch) => updateItem(index, patch)}
                              onMove={(direction) => moveItem(index, direction)}
                              onRemove={() => removeItem(index)}
                            />
                          ))}
                          <CanvasDropZone />
                        </div>
                      </SortableContext>
                    )}
                  </div>
                </>
              ) : null}
            </main>
          </div>

          <ItemTypeSidebar
            types={allowedTypes}
            quickQuestions={quickQuestions}
            quickQuestionsLoading={bankQuestionsLoading}
            sectionName={section ? section.name || 'Untitled section' : undefined}
            onSelect={addItem}
            onSelectQuestion={addFromBank}
            onOpenBank={() => setBankOpen(true)}
          />
        </div>
        <DragOverlay>
          {activeDrag ? (
            <DragItemPreview
              item={
                activeDrag.kind === 'existing'
                  ? section?.items[activeDrag.index]
                  : activeDrag.kind === 'bank'
                    ? (questionToBuilderItem(activeDrag.question, 0) ?? undefined)
                    : undefined
              }
              type={activeDrag.kind === 'new' ? activeDrag.type : undefined}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <QuestionBankPicker
        open={bankOpen}
        allowPhoto={!hideVisitOptions}
        publicOnly={surface === 'BOOKING' || surface === 'INTAKE'}
        allowedTypes={allowedTypes.map((type) =>
          type.value === 'IMAGE_UPLOAD' ? 'PHOTO' : type.value,
        )}
        onOpenChange={setBankOpen}
        onSelect={addFromBank}
      />
    </div>
  );
}

function SectionRepeatControl({
  enabled,
  options,
  onEnabledChange,
  onOptionsChange,
}: {
  enabled: boolean;
  options: string[];
  onEnabledChange: (enabled: boolean) => void;
  onOptionsChange: (options: string[]) => void;
}) {
  return (
    <Popover>
      {/* Tooltip outside, popover trigger inside. Both merge their handlers onto
          the button through Slot, and a non-DOM component sitting between a
          trigger and its button eats them: nested the other way round the
          tooltip swallowed the click and the popover never opened. */}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant={enabled ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 shrink-0 gap-1.5 px-2.5 text-[10px]"
              >
                <Repeat2 className="h-3 w-3" />
                {enabled ? `Repeatable · ${options.length}` : 'Repeatable'}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px]">
            For a section answered once per thing rather than once: name the repetitions — Unit 1,
            Unit 2, Upstairs, Downstairs — and its questions are asked again for each. Not available
            on a form the customer fills.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(380px,calc(100vw-2rem))] space-y-4 p-4"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold">Repeat this section</p>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              Create one copy of every question for each named repetition.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
            aria-label="Repeat this section"
            className="mt-0.5 shrink-0"
          />
        </div>

        {enabled ? <RepeatOptionsEditor options={options} onChange={onOptionsChange} /> : null}
      </PopoverContent>
    </Popover>
  );
}

function RepeatOptionsEditor({
  options,
  onChange,
}: {
  options: string[];
  onChange: (options: string[]) => void;
}) {
  function addOption() {
    // Empty, not "Instance 4". A row that arrives pre-filled has to be cleared
    // before it can be named, and a name nobody chose is indistinguishable
    // from one they did — the number beside the row already says which it is.
    // Blank rows are dropped on save, so an unnamed one costs nothing.
    onChange([...options, '']);
  }

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold">Named repetitions</p>
          <p className="text-[10px] text-muted-foreground">
            One row equals one copy in the checklist.
          </p>
        </div>
        <Badge variant="secondary" className="shrink-0 text-[9px]">
          {options.length} total
        </Badge>
      </div>

      <div className="space-y-2">
        {options.map((option, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background text-[10px] font-semibold text-muted-foreground">
              {index + 1}
            </span>
            <Input
              value={option}
              aria-label={`Repetition ${index + 1} name`}
              onChange={(event) =>
                onChange(
                  options.map((candidate, candidateIndex) =>
                    candidateIndex === index ? event.target.value : candidate,
                  ),
                )
              }
              placeholder={`Instance ${index + 1}`}
              className="h-8 text-xs"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={options.length === 1}
              aria-label={`Remove repetition ${index + 1}`}
              onClick={() =>
                onChange(options.filter((_, candidateIndex) => candidateIndex !== index))
              }
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addOption} className="gap-1.5">
        <Plus className="h-3.5 w-3.5" />
        Add repetition
      </Button>
    </div>
  );
}

function itemDndId(item: FormItem, index: number) {
  return `item:${item.id ?? item.placementKey ?? `${item.type}:${index}`}`;
}

function SortableBuilderItem({
  id,
  item,
  issues,
  index,
  itemCount,
  expanded,
  allowPhotoEvidence,
  surface,
  leadFieldUsage,
  onToggle,
  onChange,
  onMove,
  onRemove,
}: {
  id: string;
  item: FormItem;
  issues: BuilderIssue[];
  index: number;
  itemCount: number;
  expanded: boolean;
  allowPhotoEvidence: boolean;
  surface: FormSurface;
  leadFieldUsage: Partial<Record<LeadFieldTarget, string>>;
  onToggle: () => void;
  onChange: (patch: Partial<FormItem>) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const locked = Boolean(item.config?.locked);
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id,
    data: { kind: 'existing', index } satisfies DraggedItem,
    disabled: locked,
  });
  const meta = itemMeta(item.type);
  const Icon = meta.icon;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        'overflow-hidden rounded-xl border bg-background transition-[border-color,box-shadow,opacity]',
        expanded && 'border-foreground/25 shadow-sm',
        issues.length > 0 && 'border-destructive-border',
        isOver && !isDragging && 'border-foreground/50 ring-2 ring-foreground/10',
        isDragging && 'opacity-35',
      )}
    >
      <div className="group flex min-h-14 items-center">
        <button
          ref={setActivatorNodeRef}
          type="button"
          title="Drag to reorder"
          aria-label={`Drag ${item.label || 'untitled item'} to reorder`}
          disabled={locked}
          {...attributes}
          {...listeners}
          className="ml-2 flex h-8 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/60 hover:bg-muted hover:text-foreground active:cursor-grabbing disabled:cursor-default disabled:opacity-30"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 px-2 py-3 text-left hover:bg-muted/20"
        >
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">{item.label || 'Untitled item'}</p>
            {issues.length > 0 ? (
              issues.map((issue) => (
                <p
                  key={issue.message}
                  data-issue-anchor
                  className="mt-0.5 text-[10px] font-medium text-destructive"
                >
                  {issue.message}
                </p>
              ))
            ) : (
              <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                {itemSummary(item)}
              </p>
            )}
          </div>
          {item.required ? (
            <Badge variant="outline" className="px-1.5 text-[9px]">
              Required
            </Badge>
          ) : null}
          {locked ? (
            <Badge variant="secondary" className="px-1.5 text-[9px]">
              Contact field
            </Badge>
          ) : null}
          {item.photoRequired ? (
            <Badge
              variant="outline"
              className="border-amber-200 bg-amber-50 px-1.5 text-[9px] text-amber-700"
            >
              {Math.max(1, item.minPhotos ?? 1)} photo
            </Badge>
          ) : null}
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-180',
            )}
          />
        </button>
        {!locked ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mr-2 h-7 w-7 shrink-0 text-muted-foreground opacity-60 hover:opacity-100"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={index === 0} onSelect={() => onMove(-1)}>
                <ArrowUp className="h-3.5 w-3.5" />
                Move up
              </DropdownMenuItem>
              <DropdownMenuItem disabled={index === itemCount - 1} onSelect={() => onMove(1)}>
                <ArrowDown className="h-3.5 w-3.5" />
                Move down
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={onRemove}>
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {expanded && !locked ? (
        <InlineItemEditor
          item={item}
          onChange={onChange}
          allowPhotoEvidence={allowPhotoEvidence}
          surface={surface}
          leadFieldUsage={leadFieldUsage}
        />
      ) : expanded ? (
        <ContactFieldEditor item={item} surface={surface} onChange={onChange} />
      ) : null}
    </div>
  );
}

/**
 * Editor for a locked contact field (Name, Email, Phone number). Only the
 * wording is editable. Everything that makes it a contact field stays fixed:
 * `systemField` routes the answer to the lead's contact details, `inputType`
 * drives email/phone validation, and the server rejects the form if one is
 * missing, optional or retyped.
 */
function ContactFieldEditor({
  item,
  surface,
  onChange,
}: {
  item: FormItem;
  surface: FormSurface;
  onChange: (patch: Partial<FormItem>) => void;
}) {
  const id = useId();
  return (
    <div className="space-y-4 border-t bg-muted/10 p-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-label`} className="text-xs">
            Question
          </Label>
          <Input
            id={`${id}-label`}
            value={item.label}
            onChange={(event) => onChange({ label: event.target.value })}
            placeholder="What should be answered?"
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-placeholder`} className="text-xs">
            Placeholder
          </Label>
          <Input
            id={`${id}-placeholder`}
            value={item.config?.placeholder ?? ''}
            onChange={(event) =>
              onChange({ config: { ...(item.config ?? {}), placeholder: event.target.value } })
            }
            placeholder="Optional hint"
            className="h-8 text-xs"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-description`} className="text-xs">
          Help text
        </Label>
        <Textarea
          id={`${id}-description`}
          value={item.description ?? ''}
          onChange={(event) => onChange({ description: event.target.value })}
          rows={2}
          placeholder="Optional guidance"
          className="text-xs"
        />
      </div>
      <div className="flex items-start gap-2 rounded-md border bg-background px-3 py-2.5 text-[10px] leading-relaxed text-muted-foreground">
        <Lock className="mt-0.5 h-3 w-3 shrink-0" />
        <div>
          <p className="font-medium text-foreground">Contact field · always required</p>
          <p>Every intake form asks for it, so it can't be removed or made optional.</p>
          {surface === 'INTAKE' ? <p>Maps to lead field: Contact (automatic)</p> : null}
        </div>
      </div>
    </div>
  );
}

function CanvasDropZone({ empty = false }: { empty?: boolean }) {
  const { isOver, setNodeRef } = useDroppable({
    id: 'canvas:end',
    data: { kind: 'end' } satisfies DragTarget,
  });

  if (empty) {
    return (
      <div
        ref={setNodeRef}
        className={cn(
          'm-4 flex min-h-[360px] flex-col items-center justify-center rounded-xl border border-dashed px-6 text-center transition-colors',
          isOver ? 'border-foreground/50 bg-muted/40' : 'border-transparent',
        )}
      >
        <ClipboardList className="h-8 w-8 text-muted-foreground/45" />
        <p className="mt-3 text-sm font-medium">This section has no items</p>
        <p className="mt-1 max-w-xs text-xs text-muted-foreground">
          Drag a type here, click one in the palette, or reuse a question from the bank.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex h-11 items-center justify-center gap-2 rounded-lg border border-dashed text-[10px] transition-colors',
        isOver ? 'border-foreground/50 bg-muted/40 text-foreground' : 'text-muted-foreground',
      )}
    >
      <Plus className="h-3.5 w-3.5" />
      Drag an item here or choose a type on the right
    </div>
  );
}

function DragItemPreview({ item, type }: { item?: FormItem; type?: FormItemType }) {
  const resolvedType = item?.type ?? type ?? 'TEXT';
  const meta = itemMeta(resolvedType);
  const Icon = meta.icon;
  return (
    <div className="flex min-w-52 items-center gap-3 rounded-xl border bg-background px-4 py-3 shadow-xl">
      <GripVertical className="h-4 w-4 text-muted-foreground" />
      <Icon className="h-4 w-4 text-muted-foreground" />
      <div>
        <p className="text-xs font-medium">{item?.label || meta.label}</p>
        <p className="text-[10px] text-muted-foreground">
          {item ? itemSummary(item) : meta.description}
        </p>
      </div>
    </div>
  );
}

function InlineItemEditor({
  item,
  onChange,
  allowPhotoEvidence,
  surface,
  leadFieldUsage,
}: {
  item: FormItem;
  onChange: (patch: Partial<FormItem>) => void;
  allowPhotoEvidence: boolean;
  surface: FormSurface;
  leadFieldUsage: Partial<Record<LeadFieldTarget, string>>;
}) {
  const selectType = item.type === 'SINGLE_SELECT' || item.type === 'MULTI_SELECT';
  const numeric = item.type === 'NUMBER' || item.type === 'MEASUREMENT';
  const photoCapable = allowPhotoEvidence && item.type !== 'IMAGE_UPLOAD';
  const options = item.config?.options ?? item.options ?? [];
  const [optionDraft, setOptionDraft] = useState('');

  function updateConfig(patch: NonNullable<FormItem['config']>) {
    onChange({ config: { ...(item.config ?? {}), ...patch } });
  }

  function addOption() {
    const next = optionDraft.trim();
    if (!next || options.includes(next)) return;
    const nextOptions = [...options, next];
    onChange({ options: nextOptions, config: { ...(item.config ?? {}), options: nextOptions } });
    setOptionDraft('');
  }

  // Both keep `config.leadFieldOptionMap` (the "Maps to lead field" choice
  // targets' per-option values) in step with `options` — see
  // `withOptionRemoved`/`withOptionRenamed` next to `LeadFieldPicker`. Keyed
  // by index, not by the option's current text, so a renamed-but-not-yet-
  // committed option still resolves to the right array slot.
  function removeOptionAt(index: number) {
    const removed = options[index];
    if (removed === undefined) return;
    const nextOptions = options.filter((_, candidateIndex) => candidateIndex !== index);
    onChange({
      options: nextOptions,
      config: {
        ...(item.config ?? {}),
        options: nextOptions,
        leadFieldOptionMap: withOptionRemoved(item.config?.leadFieldOptionMap, removed),
      },
    });
  }

  // Called only on commit (blur / Enter — see `OptionChip`), never per
  // keystroke: an intermediate value while typing toward a unique name can
  // freely collide with an existing option, since nothing is written until
  // the final label is committed. Returns why a commit was accepted or
  // refused so `OptionChip` can show the right inline hint and restore the
  // old label.
  function renameOptionAt(index: number, rawLabel: string): 'accepted' | 'empty' | 'duplicate' {
    const previous = options[index];
    if (previous === undefined) return 'accepted';
    const trimmed = rawLabel.trim();
    if (!trimmed) return 'empty';
    if (trimmed === previous) return 'accepted';
    const collides = options.some(
      (candidate, candidateIndex) => candidateIndex !== index && candidate === trimmed,
    );
    if (collides) return 'duplicate';
    const nextOptions = options.map((candidate, candidateIndex) =>
      candidateIndex === index ? trimmed : candidate,
    );
    onChange({
      options: nextOptions,
      config: {
        ...(item.config ?? {}),
        options: nextOptions,
        leadFieldOptionMap: withOptionRenamed(item.config?.leadFieldOptionMap, previous, trimmed),
      },
    });
    return 'accepted';
  }

  return (
    <div className="space-y-4 border-t bg-muted/10 p-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Question</Label>
          <Textarea
            value={item.label}
            onChange={(event) => onChange({ label: event.target.value })}
            rows={2}
            placeholder="What should be answered?"
            className="text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Help text</Label>
          <Textarea
            value={item.description ?? ''}
            onChange={(event) => onChange({ description: event.target.value })}
            rows={2}
            placeholder="Optional guidance"
            className="text-xs"
          />
        </div>
      </div>
      {selectType ? (
        <InspectorGroup label="Choice options">
          <div className="flex flex-wrap items-start gap-1.5">
            {options.map((option, index) => (
              <OptionChip
                key={index}
                index={index}
                option={option}
                onRename={renameOptionAt}
                onRemove={removeOptionAt}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={optionDraft}
              onChange={(event) => setOptionDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addOption();
                }
              }}
              placeholder="Add an option"
              className="h-8 text-xs"
            />
            <Button type="button" size="sm" variant="outline" className="h-8" onClick={addOption}>
              Add
            </Button>
          </div>
        </InspectorGroup>
      ) : null}

      {numeric ? (
        <InspectorGroup
          label={item.type === 'MEASUREMENT' ? 'Measurement limits' : 'Number limits'}
        >
          <div className="grid grid-cols-2 gap-2">
            <LabeledNumber
              label="Minimum"
              value={item.config?.min}
              onChange={(min) => updateConfig({ min })}
            />
            <LabeledNumber
              label="Maximum"
              value={item.config?.max}
              onChange={(max) => updateConfig({ max })}
            />
          </div>
          {item.type === 'MEASUREMENT' ? (
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Allowed units</Label>
              <Input
                value={(item.config?.units ?? []).join(', ')}
                onChange={(event) =>
                  updateConfig({
                    units: event.target.value
                      .split(',')
                      .map((unit) => unit.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="PSI, kPa"
                className="h-8 text-xs"
              />
            </div>
          ) : null}
        </InspectorGroup>
      ) : null}

      {item.type === 'TEXT' ? (
        <InspectorGroup label="Text field">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Response style</Label>
              <Select
                value={item.config?.textStyle ?? 'long'}
                onValueChange={(textStyle) =>
                  updateConfig({ textStyle: textStyle as 'short' | 'long' })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short text</SelectItem>
                  <SelectItem value="long">Long text</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Placeholder</Label>
              <Input
                value={item.config?.placeholder ?? ''}
                onChange={(event) => updateConfig({ placeholder: event.target.value })}
                placeholder="Optional hint"
                className="h-8 text-xs"
              />
            </div>
          </div>
          <LabeledNumber
            label="Maximum characters"
            value={item.config?.maxLength}
            onChange={(maxLength) => updateConfig({ maxLength })}
          />
        </InspectorGroup>
      ) : null}

      {item.type === 'BARCODE_SCAN' ? (
        <InspectorGroup label="Barcode format">
          <Select
            value={item.config?.format ?? 'ANY'}
            onValueChange={(format) =>
              updateConfig({ format: format as 'UPC' | 'QR' | 'CODE128' | 'ANY' })
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['ANY', 'UPC', 'QR', 'CODE128'].map((format) => (
                <SelectItem key={format} value={format}>
                  {format}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </InspectorGroup>
      ) : null}

      {item.type === 'SIGNATURE' ? (
        <InspectorGroup label="Signature">
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Signer role</Label>
            <Input
              value={item.config?.signerRole ?? ''}
              onChange={(event) => updateConfig({ signerRole: event.target.value })}
              placeholder="Customer, technician"
              className="h-8 text-xs"
            />
          </div>
        </InspectorGroup>
      ) : null}

      {item.type === 'TIME' ? (
        <InspectorGroup label="Time options">
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Time interval</Label>
            <Select
              value={String(minuteStepOf(item.config))}
              onValueChange={(step) => updateConfig({ minuteStep: Number(step) as TimeMinuteStep })}
            >
              <SelectTrigger className="h-8 text-xs" aria-label="Time interval">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_MINUTE_STEPS.map((step) => (
                  <SelectItem key={step} value={String(step)}>
                    {step === 60 ? 'Every hour' : `Every ${step} minutes`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </InspectorGroup>
      ) : null}

      {item.type === 'FILE_UPLOAD' ? (
        <InspectorGroup label="File upload">
          <div className="grid grid-cols-2 gap-2">
            <LabeledNumber
              label="Maximum files"
              value={item.config?.maxFiles ?? FILE_UPLOAD_DEFAULTS.maxFiles}
              min={FILE_UPLOAD_BOUNDS.maxFiles.min}
              max={FILE_UPLOAD_BOUNDS.maxFiles.max}
              onChange={(maxFiles) => updateConfig({ maxFiles })}
            />
            <LabeledNumber
              label="Maximum size (MB)"
              value={item.config?.maxFileSizeMb ?? FILE_UPLOAD_DEFAULTS.maxFileSizeMb}
              min={FILE_UPLOAD_BOUNDS.maxFileSizeMb.min}
              max={FILE_UPLOAD_BOUNDS.maxFileSizeMb.max}
              onChange={(maxFileSizeMb) => updateConfig({ maxFileSizeMb })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Accepted files</Label>
            <Select
              value={fileUploadLimits(item.config).accept}
              onValueChange={(accept) => updateConfig({ accept: accept as FileUploadAccept })}
            >
              <SelectTrigger className="h-8 text-xs" aria-label="Accepted files">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="images_and_pdf">Images and PDFs</SelectItem>
                <SelectItem value="images">Images only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </InspectorGroup>
      ) : null}

      {item.type === 'DATE' ? (
        <InspectorGroup label="Date rules">
          <SwitchRow
            label="Allow past dates"
            description="The answer may be before today."
            checked={item.config?.allowPast ?? true}
            onCheckedChange={(allowPast) => updateConfig({ allowPast })}
          />
          <SwitchRow
            label="Allow future dates"
            description="The answer may be after today."
            checked={item.config?.allowFuture ?? true}
            onCheckedChange={(allowFuture) => updateConfig({ allowFuture })}
          />
        </InspectorGroup>
      ) : null}

      <InspectorGroup label="Completion rules">
        <div className="grid gap-3 sm:grid-cols-2">
          <SwitchRow
            label="Required"
            description="Must be answered before this form can be completed."
            checked={item.required}
            onCheckedChange={(required) => onChange({ required })}
          />
          <LeadFieldPicker
            item={item}
            surface={surface}
            usage={leadFieldUsage}
            onChange={onChange}
          />
          {photoCapable ? (
            <SwitchRow
              label="Photos required"
              description="Evidence stays grouped with this answer."
              checked={Boolean(item.photoRequired)}
              onCheckedChange={(photoRequired) => onChange({ photoRequired })}
            />
          ) : null}
          {allowPhotoEvidence && (item.photoRequired || item.type === 'IMAGE_UPLOAD') ? (
            <div className="grid grid-cols-2 gap-2">
              <LabeledNumber
                label="Minimum photos"
                value={item.minPhotos ?? 1}
                min={1}
                onChange={(minPhotos) =>
                  onChange({
                    minPhotos: minPhotos ?? 1,
                    config: { ...(item.config ?? {}), minPhotos: minPhotos ?? 1 },
                  })
                }
              />
              <LabeledNumber
                label="Maximum photos"
                value={item.config?.maxPhotos}
                min={1}
                onChange={(maxPhotos) => updateConfig({ maxPhotos })}
              />
            </div>
          ) : null}
        </div>
      </InspectorGroup>

      {item.isLibraryItem ? (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-[10px] leading-relaxed text-blue-800">
          This item stays linked to a reusable question version. Changes here apply only to this
          template and do not rewrite the question in the bank.
        </div>
      ) : null}
    </div>
  );
}

/**
 * One "Choice options" chip: an editable label plus a remove button. Edits
 * live in a local draft and only commit (call `onRename`) on blur or Enter —
 * never per keystroke — so a value typed *through* a collision with another
 * option (e.g. "Today" while typing toward "Tomorrow") never touches state.
 * A refused commit (empty, or a duplicate of a sibling option) restores the
 * pre-edit label and shows a small inline hint; nothing is written to
 * `options` or `config.leadFieldOptionMap` either way.
 */
function OptionChip({
  index,
  option,
  onRename,
  onRemove,
}: {
  index: number;
  option: string;
  onRename: (index: number, nextLabel: string) => 'accepted' | 'empty' | 'duplicate';
  onRemove: (index: number) => void;
}) {
  const [draft, setDraft] = useState(option);
  const [error, setError] = useState<string | null>(null);

  // The option can also change from outside (reorder, another edit) — stay
  // in sync and drop any stale error when that happens.
  useEffect(() => {
    setDraft(option);
    setError(null);
  }, [option]);

  function commit() {
    const result = onRename(index, draft);
    if (result === 'accepted') {
      setDraft((current) => current.trim());
      setError(null);
      return;
    }
    setDraft(option);
    setError(result === 'empty' ? "Option names can't be empty." : 'Option names must be unique.');
  }

  return (
    <div className="flex flex-col items-start gap-0.5">
      <Badge variant="secondary" className="gap-1 pr-1 text-[10px]">
        <input
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            if (error) setError(null);
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
          aria-label={`Option ${index + 1}`}
          size={Math.max(draft.length, 1)}
          className="max-w-[12rem] border-0 bg-transparent p-0 text-[10px] outline-none focus:ring-0"
        />
        <button
          type="button"
          className="rounded p-0.5 hover:bg-muted"
          aria-label={`Remove option ${option}`}
          onClick={() => onRemove(index)}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </Badge>
      {error ? <p className="text-[9px] text-destructive">{error}</p> : null}
    </div>
  );
}

function TemplateSettings({
  value,
  onChange,
  hideVisitOptions,
  surface,
  bookableServices,
  onToggleBookableService,
  intakeLeadSource,
}: {
  value: FormDefinitionBuilderValue;
  onChange: (patch: Partial<FormDefinitionBuilderValue>) => void;
  hideVisitOptions: boolean;
  surface: 'CHECKLIST' | 'VISIT_FORM' | 'BOOKING' | 'INTAKE';
  bookableServices: Array<{ id: string; name: string; isActive: boolean }>;
  onToggleBookableService: (id: string) => void;
  intakeLeadSource?: { templateId: string; acquisitionChannelId: string | null };
}) {
  return (
    <div className="space-y-5 p-5">
      <div className="space-y-1.5">
        <Label className="text-xs">Name</Label>
        <Input
          value={value.name}
          onChange={(event) => onChange({ name: event.target.value })}
          placeholder="Template name"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Description</Label>
        <Textarea
          value={value.description}
          onChange={(event) => onChange({ description: event.target.value })}
          rows={3}
          placeholder="When and why this form is used"
        />
      </div>
      {surface === 'CHECKLIST' ? (
        <div className="space-y-1.5">
          <Label className="text-xs">Default visibility</Label>
          <Select
            value={value.defaultVisibility ?? 'INTERNAL'}
            onValueChange={(defaultVisibility) =>
              onChange({
                defaultVisibility: defaultVisibility as 'INTERNAL' | 'CUSTOMER_VISIBLE',
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INTERNAL">Internal only</SelectItem>
              <SelectItem value="CUSTOMER_VISIBLE">Customer visible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : !hideVisitOptions ? (
        <SwitchRow
          label="Allow multiple submissions"
          description="Use for repeated readings or events on the same visit."
          checked={value.allowMultipleSubmissions}
          onCheckedChange={(allowMultipleSubmissions) => onChange({ allowMultipleSubmissions })}
        />
      ) : surface === 'BOOKING' ? (
        <>
          <div className="rounded-lg border bg-muted/25 p-3 text-xs text-muted-foreground">
            Booking surveys are public intake. They never block booking and do not expose camera,
            barcode, signature, or offline tooling.
          </div>
          <BookableServicePicker
            value={value}
            services={bookableServices}
            onToggle={onToggleBookableService}
          />
        </>
      ) : (
        <>
          <div className="rounded-lg border bg-muted/25 p-3 text-xs leading-relaxed text-muted-foreground">
            Intake forms are customer-facing and accept multiple responses. Publish once, then share
            the hosted link or iframe from the template detail page.
          </div>
          <IntakePublicPageSettings
            value={value.publicSettings ?? defaultPublicSettingsDraft()}
            onChange={(publicSettings) => onChange({ publicSettings })}
            templateId={intakeLeadSource?.templateId}
            acquisitionChannelId={intakeLeadSource?.acquisitionChannelId}
          />
        </>
      )}
    </div>
  );
}

function BookableServicePicker({
  value,
  services,
  onToggle,
}: {
  value: FormDefinitionBuilderValue;
  services: Array<{ id: string; name: string; isActive: boolean }>;
  onToggle: (id: string) => void;
}) {
  const activeServices = services.filter((service) => service.isActive);
  return (
    <div className="space-y-2 border-t pt-4">
      <div>
        <Label className="text-xs">Booking services</Label>
        <p className="text-[10px] text-muted-foreground">
          Leave empty to use this survey for every booking service.
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {activeServices.length ? (
          activeServices.map((service) => {
            const active = value.bookableServiceIds.includes(service.id);
            return (
              <button
                key={service.id}
                type="button"
                onClick={() => onToggle(service.id)}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-[10px] font-medium',
                  active ? 'border-foreground bg-foreground text-background' : 'hover:bg-muted',
                )}
              >
                {service.name}
              </button>
            );
          })
        ) : (
          <span className="text-xs text-muted-foreground">No active booking services</span>
        )}
      </div>
    </div>
  );
}

function ItemTypeSidebar({
  types,
  quickQuestions,
  quickQuestionsLoading,
  sectionName,
  onSelect,
  onSelectQuestion,
  onOpenBank,
}: {
  types: typeof ITEM_TYPES;
  quickQuestions: QuestionDefinition[];
  quickQuestionsLoading: boolean;
  sectionName?: string;
  onSelect: (type: FormItemType) => void;
  onSelectQuestion: (question: QuestionDefinition) => void;
  onOpenBank: () => void;
}) {
  return (
    <aside className="border-t bg-muted/10 xl:border-l xl:border-t-0">
      <div className="flex h-14 items-center border-b bg-background px-4">
        <div>
          <p className="text-xs font-semibold">Add an item</p>
          <p className="text-[10px] text-muted-foreground">
            {sectionName ? `Drag into ${sectionName} or click` : 'Choose a section first'}
          </p>
        </div>
      </div>
      <div className="p-4 xl:sticky xl:top-3">
        <div className="grid grid-cols-2 gap-2">
          {types.map((type) => (
            <PaletteTypeButton
              key={type.value}
              type={type}
              disabled={!sectionName}
              onSelect={onSelect}
            />
          ))}
        </div>

        <div className="mt-4 border-t pt-4">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold">Quick questions</p>
              <p className="text-[10px] text-muted-foreground">Most used from your bank</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!sectionName}
              onClick={onOpenBank}
              className="h-7 gap-1 px-2 text-[10px]"
            >
              More
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>

          <div className="space-y-1.5">
            {quickQuestionsLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="h-11 animate-pulse rounded-lg border bg-background/60"
                />
              ))
            ) : quickQuestions.length ? (
              quickQuestions.map((question) => (
                <QuickQuestionButton
                  key={question.id}
                  question={question}
                  disabled={!sectionName}
                  onSelect={onSelectQuestion}
                />
              ))
            ) : (
              <button
                type="button"
                disabled={!sectionName}
                onClick={onOpenBank}
                className="flex w-full items-center gap-3 rounded-lg border border-dashed bg-background px-3 py-3 text-left transition-colors hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                  <Library className="h-4 w-4 text-muted-foreground" />
                </span>
                <span>
                  <span className="block text-xs font-medium">Add a reusable question</span>
                  <span className="block text-[10px] text-muted-foreground">
                    Your question bank is empty
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>

        {sectionName ? (
          <p className="mt-3 rounded-lg bg-muted px-3 py-2.5 text-[10px] leading-relaxed text-muted-foreground">
            Drag a type to place it exactly. Clicking adds it to the end. Use the grip on an item to
            reorder it.
          </p>
        ) : null}
      </div>
    </aside>
  );
}

function QuickQuestionButton({
  question,
  disabled,
  onSelect,
}: {
  question: QuestionDefinition;
  disabled: boolean;
  onSelect: (question: QuestionDefinition) => void;
}) {
  const item = questionToBuilderItem(question, 0);
  const meta = itemMeta(item?.type ?? 'TEXT');
  const Icon = meta.icon;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `bank:${question.id}`,
    data: { kind: 'bank', question } satisfies DraggedItem,
    disabled: disabled || !item,
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      disabled={disabled || !item}
      onClick={() => onSelect(question)}
      {...attributes}
      {...listeners}
      className={cn(
        'flex h-12 w-full cursor-grab touch-none items-center gap-2.5 rounded-lg border bg-background px-3 text-left shadow-sm transition-[border-color,background-color,opacity] hover:border-foreground/20 hover:bg-muted/30 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50',
        isDragging && 'opacity-35',
      )}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium">{item?.label || question.name}</span>
        <span className="block text-[10px] text-muted-foreground">
          {meta.label}
          {question.usageCount ? ` · used ${question.usageCount}×` : ''}
        </span>
      </span>
      <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/45" />
    </button>
  );
}

function PaletteTypeButton({
  type,
  disabled,
  onSelect,
}: {
  type: (typeof ITEM_TYPES)[number];
  disabled: boolean;
  onSelect: (type: FormItemType) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `new:${type.value}`,
    data: { kind: 'new', type: type.value } satisfies DraggedItem,
    disabled,
  });
  const Icon = type.icon;
  return (
    <button
      ref={setNodeRef}
      type="button"
      title={type.description}
      disabled={disabled}
      onClick={() => onSelect(type.value)}
      {...attributes}
      {...listeners}
      className={cn(
        'flex h-11 cursor-grab touch-none items-center gap-2 rounded-lg border bg-background px-3 text-left text-xs font-medium shadow-sm transition-[border-color,background-color,opacity] hover:border-foreground/20 hover:bg-muted/30 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50',
        isDragging && 'opacity-35',
      )}
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="truncate">{type.label}</span>
    </button>
  );
}

function InspectorGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

function SwitchRow({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <Switch checked={checked} onCheckedChange={onCheckedChange} className="mt-0.5 shrink-0" />
      <span className="min-w-0">
        <span className="block text-xs font-medium">{label}</span>
        <span className="mt-0.5 block text-[10px] leading-relaxed text-muted-foreground">
          {description}
        </span>
      </span>
    </label>
  );
}

function LabeledNumber({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number | undefined;
  min?: number;
  max?: number;
  onChange: (value: number | undefined) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] text-muted-foreground">{label}</Label>
      <Input
        type="number"
        min={min}
        max={max}
        value={value ?? ''}
        onChange={(event) =>
          onChange(event.target.value === '' ? undefined : Number(event.target.value))
        }
        className="h-8 text-xs"
      />
    </div>
  );
}

function itemMeta(type: FormItemType) {
  return ITEM_TYPES.find((candidate) => candidate.value === type) ?? ITEM_TYPES[0];
}

/** The palette's name for an item type, for read-only views of a form. */
// eslint-disable-next-line react-refresh/only-export-components
export function itemTypeLabel(type: FormItemType): string {
  return itemMeta(type).label;
}

function itemSummary(item: FormItem) {
  const summary = [itemMeta(item.type).label];
  if (item.type === 'MEASUREMENT' && item.config?.units?.length) {
    summary.push(item.config.units.join(' / '));
  }
  if (
    (item.type === 'SINGLE_SELECT' || item.type === 'MULTI_SELECT') &&
    (item.config?.options?.length || item.options?.length)
  ) {
    summary.push(`${item.config?.options?.length ?? item.options?.length ?? 0} options`);
  }
  if (item.config?.min !== undefined || item.config?.max !== undefined) {
    summary.push(`${item.config.min ?? '–'}–${item.config.max ?? '–'}`);
  }
  if (item.type === 'TIME') {
    const step = minuteStepOf(item.config);
    summary.push(step === 60 ? 'Hourly' : `Every ${step} min`);
  }
  if (item.type === 'FILE_UPLOAD') {
    const limits = fileUploadLimits(item.config);
    summary.push(
      `Up to ${limits.maxFiles} ${limits.maxFiles === 1 ? 'file' : 'files'} · ${limits.maxFileSizeMb} MB`,
    );
  }
  return summary.join(' · ');
}

function defaultConfig(type: FormItemType): FormItem['config'] {
  switch (type) {
    case 'SINGLE_SELECT':
    case 'MULTI_SELECT':
      return { options: [] };
    case 'MEASUREMENT':
      return { units: [] };
    case 'BARCODE_SCAN':
      return { format: 'ANY' };
    case 'TIME':
      return { minuteStep: DEFAULT_MINUTE_STEP };
    case 'FILE_UPLOAD':
      return { ...FILE_UPLOAD_DEFAULTS };
    default:
      return null;
  }
}
